import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Mail, Code, Copy } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов страницы
import GeneratorForm from './MailtoLinkGeneratorPage/components/GeneratorForm';
import ResultDisplay from './MailtoLinkGeneratorPage/components/ResultDisplay';
import PreviewDisplay from './MailtoLinkGeneratorPage/components/PreviewDisplay';
import QRCodeDisplay from './MailtoLinkGeneratorPage/components/QRCodeDisplay';
import UsageTips from './MailtoLinkGeneratorPage/components/UsageTips';
import TemplatesSidebar from './MailtoLinkGeneratorPage/components/TemplatesSidebar';
import StatisticsSidebar from './MailtoLinkGeneratorPage/components/StatisticsSidebar';
import InfoContent from './MailtoLinkGeneratorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  to: '',
  cc: '',
  bcc: '',
  subject: '',
  body: '',
  includeGoogleAnalytics: false,
  gaTrackingId: '',
  trackOpens: false,
  useUtmParameters: false,
  utmSource: 'email',
  utmMedium: 'mailto',
  utmCampaign: '',
  useCyrillicEncoding: true,
  addLineBreaks: true,
  addSignature: false,
  signature: '',
  companyName: '',
  companyWebsite: '',
  companyPhone: '',
};

// Определение шаблонов писем
export const emailTemplates = [
  {
    id: 'feedback',
    name: 'Запрос обратной связи',
    subject: 'Ваше мнение важно для нас',
    body: 'Здравствуйте!\n\nНам очень важно ваше мнение о нашем продукте/услуге. Пожалуйста, поделитесь своими впечатлениями.\n\nС уважением,'
  },
  {
    id: 'support',
    name: 'Техподдержка',
    subject: 'Запрос в техническую поддержку',
    body: 'Здравствуйте!\n\nМне требуется помощь по следующему вопросу:\n\n[Опишите проблему]\n\nИнформация о системе:\n- Устройство:\n- Операционная система:\n- Версия программы:\n\nС уважением,'
  },
  {
    id: 'request',
    name: 'Коммерческий запрос',
    subject: 'Запрос информации о продукте/услуге',
    body: 'Здравствуйте!\n\nМеня интересует информация о [Название продукта/услуги].\n\nПожалуйста, предоставьте:\n- Стоимость\n- Условия\n- Сроки\n\nЗаранее благодарю за ответ.\n\nС уважением,'
  },
  {
    id: 'job',
    name: 'Отклик на вакансию',
    subject: 'Отклик на вакансию [Название вакансии]',
    body: 'Здравствуйте!\n\nЯ заинтересован(а) в вакансии [Название вакансии], размещенной на [Источник].\n\nМой опыт работы включает:\n- [Опыт 1]\n- [Опыт 2]\n- [Опыт 3]\n\nМое резюме прикреплено к письму.\n\nС уважением,'
  },
  {
    id: 'partnership',
    name: 'Предложение сотрудничества',
    subject: 'Предложение о сотрудничестве',
    body: 'Здравствуйте!\n\nПредлагаю рассмотреть возможность сотрудничества между нашими компаниями.\n\nНаша компания специализируется на [Сфера деятельности].\n\nМы можем предложить следующие варианты сотрудничества:\n- [Вариант 1]\n- [Вариант 2]\n- [Вариант 3]\n\nБуду рад обсудить детали при личной встрече или по телефону.\n\nС уважением,'
  }
];

/**
 * MailtoLinkGeneratorPage Component - Полный генератор mailto-ссылок с различными параметрами.
 */
const MailtoLinkGeneratorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('mailto-link-generator'), []);
  const pageTitle = tool?.metaTitle || 'Генератор Mailto-ссылок | Gensio';
  const metaDescription = tool?.metaDescription || 'Создайте mailto-ссылки для ваших контактных форм, email-маркетинга и подписей электронной почты. Поддержка кириллицы, UTM-меток и предварительного заполнения полей.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [mailtoLink, setMailtoLink] = useState('');
  const [htmlCode, setHtmlCode] = useState('');
  const [markdownCode, setMarkdownCode] = useState('');
  const [errors, setErrors] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [copyType, setCopyType] = useState('');
  const [usageStats, setUsageStats] = useState({
    totalGeneratedLinks: 0,
    totalCopiedLinks: 0,
    totalQrCodesDownloaded: 0,
    mostUsedTemplate: null
  });

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных шаблонов из localStorage ---
  useEffect(() => {
    const savedTemplatesData = localStorage.getItem('mailtoSavedTemplates');
    if (savedTemplatesData) {
      try {
        setSavedTemplates(JSON.parse(savedTemplatesData));
      } catch (e) {
        console.error("Error loading saved templates:", e);
      }
    }

    // Загрузка статистики использования
    const statsData = localStorage.getItem('mailtoUsageStats');
    if (statsData) {
      try {
        setUsageStats(JSON.parse(statsData));
      } catch (e) {
        console.error("Error loading usage statistics:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'feedback':
          newState = {
            ...newState,
            subject: 'Запрос обратной связи',
            body: 'Здравствуйте!\n\nМне хотелось бы оставить следующий отзыв:\n\n\n\nС уважением,'
          };
          break;
        case 'support':
          newState = {
            ...newState,
            subject: 'Запрос в техническую поддержку',
            body: 'Здравствуйте!\n\nМне требуется помощь с:\n\n\n\nТехническая информация:\n\n\nС уважением,'
          };
          break;
        case 'analytics':
          newState = {
            ...newState,
            includeGoogleAnalytics: true,
            gaTrackingId: 'UA-XXXXXXXX-X',
            trackOpens: true,
            useUtmParameters: true
          };
          break;
        case 'signature':
          newState = {
            ...newState,
            addSignature: true,
            signature: 'Иван Иванов\nМенеджер по продажам',
            companyName: 'ООО "Компания"',
            companyWebsite: 'https://www.example.ru',
            companyPhone: '+7 (999) 123-45-67'
          };
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setMailtoLink('');
    setHtmlCode('');
    setMarkdownCode('');
    setErrors({});

  }, [subToolId]);

  // Сохранение шаблона в localStorage
  const saveTemplate = (name) => {
    if (!mailtoLink) return;

    const newTemplate = {
      id: Date.now().toString(),
      name: name || `Шаблон от ${new Date().toLocaleDateString('ru-RU')}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      mailtoLink
    };

    const updatedTemplates = [...savedTemplates, newTemplate];
    setSavedTemplates(updatedTemplates);
    localStorage.setItem('mailtoSavedTemplates', JSON.stringify(updatedTemplates));

    // Обновление статистики
    const updatedStats = {
      ...usageStats,
      totalGeneratedLinks: usageStats.totalGeneratedLinks + 1
    };
    setUsageStats(updatedStats);
    localStorage.setItem('mailtoUsageStats', JSON.stringify(updatedStats));

    return newTemplate;
  };

  // Удаление сохраненного шаблона
  const deleteTemplate = (id) => {
    const updatedTemplates = savedTemplates.filter(template => template.id !== id);
    setSavedTemplates(updatedTemplates);
    localStorage.setItem('mailtoSavedTemplates', JSON.stringify(updatedTemplates));
  };

  // Загрузка сохраненного шаблона
  const loadTemplate = (id) => {
    const templateToLoad = savedTemplates.find(template => template.id === id);
    if (templateToLoad) {
      setFormData(templateToLoad.formData);
      setMailtoLink(templateToLoad.mailtoLink);
      generateCodes(templateToLoad.mailtoLink);
    }
  };

  // Копирование ссылки или кода
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopyType(type);
      setShowCopiedMessage(true);
      
      // Обновление статистики
      const updatedStats = {
        ...usageStats,
        totalCopiedLinks: usageStats.totalCopiedLinks + 1
      };
      setUsageStats(updatedStats);
      localStorage.setItem('mailtoUsageStats', JSON.stringify(updatedStats));
      
      setTimeout(() => setShowCopiedMessage(false), 2000);
    }).catch(err => {
      console.error('Не удалось скопировать: ', err);
    });
  };

  // Загрузка QR-кода
  const handleQRDownload = () => {
    // Обновление статистики
    const updatedStats = {
      ...usageStats,
      totalQrCodesDownloaded: usageStats.totalQrCodesDownloaded + 1
    };
    setUsageStats(updatedStats);
    localStorage.setItem('mailtoUsageStats', JSON.stringify(updatedStats));
  };

  // Генерация HTML и Markdown кодов
  const generateCodes = (link) => {
    if (!link) return;
    
    const linkText = formData.subject || 'Написать письмо';
    setHtmlCode(`<a href="${link}">${linkText}</a>`);
    setMarkdownCode(`[${linkText}](${link})`);
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Mail className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Генератор Mailto-ссылок'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма генератора --- */}
            <GeneratorForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isGenerating={isGenerating}
              setIsGenerating={setIsGenerating}
              setMailtoLink={setMailtoLink}
              generateCodes={generateCodes}
              emailTemplates={emailTemplates}
              saveTemplate={saveTemplate}
            />

            {/* --- Результаты генерации --- */}
            {mailtoLink && !isGenerating && (
              <>
                <ResultDisplay 
                  mailtoLink={mailtoLink}
                  htmlCode={htmlCode}
                  markdownCode={markdownCode}
                  copyToClipboard={copyToClipboard}
                  showCopiedMessage={showCopiedMessage}
                  copyType={copyType}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                  <PreviewDisplay mailtoLink={mailtoLink} formData={formData} />
                  <QRCodeDisplay 
                    mailtoLink={mailtoLink} 
                    onDownload={handleQRDownload} 
                  />
                </div>
              </>
            )}

            {/* --- Информационный контент о mailto-ссылках --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель шаблонов --- */}
            <TemplatesSidebar 
              emailTemplates={emailTemplates}
              setFormData={setFormData}
              setErrors={setErrors}
              savedTemplates={savedTemplates}
              deleteTemplate={deleteTemplate}
              loadTemplate={loadTemplate}
            />

            {/* --- Панель статистики --- */}
            <StatisticsSidebar 
              stats={usageStats}
            />

            {/* --- Полезные советы --- */}
            <UsageTips />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default MailtoLinkGeneratorPage;